// src/components/modals/TicketUpdateModal.jsx

import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { FaCalendarAlt, FaTicketAlt, FaMoneyBillWave, FaListOl, FaInfoCircle } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import { toast } from 'react-toastify';

// Bind modal to your appElement for accessibility
Modal.setAppElement('#root');

const TicketUpdateModal = ({ isOpen, onRequestClose, onSubmit, initialValues, ticketToUpdate, yourEvents }) => {
  const [categories, setCategories] = useState([]);

  // Define comprehensive ticket categories list
  useEffect(() => {
    // Extended list of ticket categories - MUST match backend CATEGORY_CHOICES exactly
    const availableCategories = [
      { value: 'General', label: 'General' },
      { value: 'Free', label: 'Free' },
      { value: 'Early Bird', label: 'Early Bird' },
      { value: 'Vip', label: 'VIP/Premium' },
      { value: 'Front Row', label: 'Front Row' },
      { value: 'Member', label: 'Member' },
      { value: 'Group', label: 'Group' },
      { value: 'Gold', label: 'Gold' },
      { value: 'Silver', label: 'Silver' },
      { value: 'Bronze', label: 'Bronze' },
      { value: 'Student', label: 'Student' },
      { value: 'Senior', label: 'Senior' },
      // Keep custom option
      { value: 'custom', label: 'Custom Category...' }
    ];
    
    setCategories(availableCategories);
  }, []);

  // Define validation schema
  const validationSchema = Yup.object().shape({
    event: Yup.string().required("Event is required"),
    ticket_category: Yup.string().required("Ticket category is required"),
    price: Yup.number()
      .typeError("Price must be a number")
      .min(0, "Price must be at least 0")
      .required("Price is required"),
    total_quantity: Yup.number()
      .typeError("Quantity must be a number")
      .integer("Quantity must be a whole number")
      .min(1, "Quantity must be at least 1")
      .required("Quantity is required"),
    ticket_description: Yup.string().required("Description is required"),
  });

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="Update Ticket"
      className="flex justify-center items-center p-3 md:p-6"
      overlayClassName="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-50"
    >
      <div className="bg-white p-5 md:p-8 rounded-xl shadow-2xl w-full max-w-2xl relative overflow-y-auto max-h-[90vh] animate-modal-fade-in">
        {/* Close Button */}
        <button
          onClick={onRequestClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition-colors"
          aria-label="Close modal"
        >
          <MdClose className="w-6 h-6" />
        </button>

        <Formik
          initialValues={
            ticketToUpdate
              ? {
                event: ticketToUpdate.event,
                eventName:
                  yourEvents?.data?.results && Array.isArray(yourEvents.data.results) ? 
                  yourEvents.data.results.find((event) => event.event_id === ticketToUpdate.event)
                    ?.event_name || 'N/A' : 'N/A',
                ticket_category: ticketToUpdate.ticket_category,
                price: ticketToUpdate.price,
                total_quantity: ticketToUpdate.total_quantity,
                ticket_description: ticketToUpdate.ticket_description,
              }
              : {
                event: '',
                eventName: '',
                ticket_category: '',
                price: '',
                total_quantity: '',
                ticket_description: '',
              }
          }
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              // If using custom category, use the custom_category value as the ticket_category
              const dataToSubmit = {...values};
              
              if (values.ticket_category === 'custom' && values.custom_category) {
                // For custom categories, send the custom value directly
                dataToSubmit.ticket_category = values.custom_category;
              }
              
              // Log the data being sent to help with debugging
              console.log("Submitting ticket update:", dataToSubmit);
              
              // Remove any fields the API doesn't expect
              delete dataToSubmit.eventName;
              delete dataToSubmit.custom_category;
              
              await onSubmit(dataToSubmit);
              setSubmitting(false);
              onRequestClose();
            } catch (error) {
              console.error("Error updating ticket:", error);
              toast.error("Failed to update ticket. Please try again.");
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting, values, setFieldValue }) => (
            <Form className="w-full">
              {/* Title */}
              <div className="border-b border-gray-200 pb-4 mb-6">
                <h2 className="text-2xl font-semibold text-gray-800">Update Ticket</h2>
                <p className="text-gray-500 mt-1">Make changes to your ticket details below</p>
                
                {/* Capacity Information */}
                <div className="mt-3 bg-blue-50 p-3 rounded-lg border border-blue-100">
                  <div className="flex justify-between items-center text-sm">
                    <span>Event Capacity:</span>
                    <span className="font-medium">{ticketToUpdate?.event_capacity || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm mt-1">
                    <span>Available Tickets:</span>
                    <span className="font-medium text-[#004B95]">
                      {ticketToUpdate?.available_tickets || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                {/* Event Name (Disabled) */}
                <div className="mb-3">
                  <label htmlFor="eventName" className="block mb-2 text-sm font-medium text-gray-700">
                    Event <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FaCalendarAlt className="absolute left-3 top-3.5 text-gray-500 w-4 h-4" />
                    <Field
                      type="text"
                      id="eventName"
                      name="eventName" // Separate name for display
                      value={initialValues.eventName}
                      disabled
                      className="w-full p-2.5 pl-9 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 cursor-not-allowed"
                    />
                  </div>
                  <ErrorMessage name="event" component="div" className="text-red-500 text-xs mt-1" />
                </div>

                {/* Hidden Event ID */}
                <Field type="hidden" name="event" />

                {/* Ticket Category - Enhanced with more options */}
                <div className="mb-3">
                  <label htmlFor="ticket_category" className="block mb-2 text-sm font-medium text-gray-700">
                    Ticket Category <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FaTicketAlt className="absolute left-3 top-3.5 text-gray-500 w-4 h-4" />
                    <Field
                      as="select"
                      id="ticket_category"
                      name="ticket_category"
                      className="w-full p-2.5 pl-9 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer text-gray-700"
                    >
                      <option value="" disabled>Select category</option>
                      {categories.map(category => (
                        <option 
                          key={category.value} 
                          value={category.value}
                        >
                          {category.label}
                        </option>
                      ))}
                      <option value="custom">Custom Category...</option>
                    </Field>
                    <div className="pointer-events-none absolute right-3 top-3.5">
                      <svg className="h-4 w-4 fill-current text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                      </svg>
                    </div>
                  </div>
                  <ErrorMessage name="ticket_category" component="div" className="text-red-500 text-xs mt-1" />

                  {/* Custom Category Input Field (shown conditionally) */}
                  {values.ticket_category === 'custom' && (
                    <div className="mt-2">
                      <input
                        type="text"
                        placeholder="Enter custom category name"
                        className="w-full p-2.5 pl-9 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onChange={(e) => {
                          setFieldValue('custom_category', e.target.value);
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Price - Improved with currency symbol */}
                <div className="mb-3">
                  <label htmlFor="price" className="block mb-2 text-sm font-medium text-gray-700">
                    Price <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-3.5 text-gray-500">£</div>
                    <Field
                      type="number"
                      id="price"
                      name="price"
                      placeholder="0.00"
                      className="w-full p-2.5 pl-7 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <ErrorMessage name="price" component="div" className="text-red-500 text-xs mt-1" />
                  <div className="mt-1 text-xs text-gray-500">
                    Free tickets should be set to 0.00
                  </div>
                </div>

                {/* Total Quantity */}
                <div className="mb-3">
                  <label htmlFor="total_quantity" className="block mb-2 text-sm font-medium text-gray-700">
                    Quantity <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FaListOl className="absolute left-3 top-3.5 text-gray-500 w-4 h-4" />
                    <Field
                      type="number"
                      id="total_quantity"
                      name="total_quantity"
                      placeholder="10"
                      className="w-full p-2.5 pl-9 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                      min="1"
                    />
                  </div>
                  <ErrorMessage name="total_quantity" component="div" className="text-red-500 text-xs mt-1" />
                </div>

                {/* Ticket Description - Full width */}
                <div className="mb-3 md:col-span-2">
                  <label htmlFor="ticket_description" className="block mb-2 text-sm font-medium text-gray-700">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FaInfoCircle className="absolute left-3 top-3.5 text-gray-500 w-4 h-4" />
                    <Field
                      as="textarea"
                      id="ticket_description"
                      name="ticket_description"
                      placeholder="Describe what this ticket includes..."
                      className="w-full p-2.5 pl-9 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none h-24 text-gray-700"
                    />
                  </div>
                  <ErrorMessage name="ticket_description" component="div" className="text-red-500 text-xs mt-1" />
                </div>

              </div>

              {/* Footer - Buttons */}
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onRequestClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-150 focus:outline-none focus:ring-2 focus:ring-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-4 py-2 bg-[#004B95] rounded-lg text-white hover:bg-[#003D7A] transition duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? "Updating..." : "Update Ticket"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
};

TicketUpdateModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    event: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, // Event ID
    eventName: PropTypes.string.isRequired, // Event Name for display
    ticket_category: PropTypes.string, // Changed to not required
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    total_quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    ticket_description: PropTypes.string.isRequired,
  }).isRequired,
  ticketToUpdate: PropTypes.shape({
    event: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    ticket_category: PropTypes.string, // Changed to not required
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    total_quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    ticket_description: PropTypes.string.isRequired,
  }),
  yourEvents: PropTypes.shape({
    data: PropTypes.shape({
      results: PropTypes.array,
    }),
  }),
};

export default TicketUpdateModal;
